import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';

// Éclat skeleton surface: keep animate-pulse (from base Skeleton), soft sky fill
const S = 'bg-secondary/70 rounded-xl';

// Provider card skeleton
export function ProviderCardSkeleton() {
  return (
    <div className="bg-card border border-border/70 overflow-hidden rounded-2xl shadow-soft" data-testid="skeleton-card">
      <Skeleton className={`${S} h-40 w-full rounded-none`} />
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className={`${S} h-3 w-20`} />
          <Skeleton className={`${S} h-3 w-16`} />
        </div>
        <Skeleton className={`${S} h-4 w-3/4`} />
        <Skeleton className={`${S} h-3 w-full`} />
        <div className="flex justify-between pt-1">
          <Skeleton className={`${S} h-4 w-16`} />
          <Skeleton className={`${S} h-3 w-20`} />
        </div>
        <div className="flex gap-2 pt-2">
          <Skeleton className={`${S} h-8 flex-1`} />
          <Skeleton className={`${S} h-8 flex-1`} />
        </div>
      </div>
    </div>
  );
}

// Grid of provider card skeletons
export function ProviderGridSkeleton({ count = 6 }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {Array.from({ length: count }).map((_, i) => <ProviderCardSkeleton key={`sk-${i}`} />)}
    </div>
  );
}

// Table skeleton
export function TableSkeleton({ rows = 5, cols = 5 }) {
  return (
    <div className="bg-card border border-border/70 rounded-2xl shadow-soft overflow-hidden" data-testid="skeleton-table">
      <div className="bg-secondary/40 border-b border-border/70 px-4 py-3 flex gap-4">
        {Array.from({ length: cols }).map((_, i) => <Skeleton key={`th-${i}`} className={`${S} h-3 w-20`} />)}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={`tr-${r}`} className="px-4 py-3 border-b border-border/50 flex gap-4">
          {Array.from({ length: cols }).map((_, c) => <Skeleton key={`td-${r}-${c}`} className={`${S} h-3`} style={{ width: `${60 + (c * 15) % 40}px` }} />)}
        </div>
      ))}
    </div>
  );
}

// Dashboard stat card skeleton
export function StatCardSkeleton({ count = 4 }) {
  return (
    <div className={`grid grid-cols-2 md:grid-cols-${count} gap-3`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={`stat-${i}`} className="bg-card border border-border/70 p-4 rounded-2xl shadow-soft space-y-2">
          <Skeleton className={`${S} h-2.5 w-16`} />
          <Skeleton className={`${S} h-6 w-12`} />
          <Skeleton className={`${S} h-2 w-20`} />
        </div>
      ))}
    </div>
  );
}

// Profile page skeleton
export function ProfileSkeleton() {
  return (
    <div className="space-y-6" data-testid="skeleton-profile">
      <div className="flex items-start gap-4">
        <Skeleton className={`${S} h-16 w-16 rounded-full`} />
        <div className="space-y-2 flex-1">
          <Skeleton className={`${S} h-5 w-48`} />
          <Skeleton className={`${S} h-3 w-32`} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Skeleton className={`${S} h-3 w-16`} /><Skeleton className={`${S} h-10 w-full`} /></div>
        <div className="space-y-2"><Skeleton className={`${S} h-3 w-16`} /><Skeleton className={`${S} h-10 w-full`} /></div>
      </div>
      <div className="space-y-2"><Skeleton className={`${S} h-3 w-16`} /><Skeleton className={`${S} h-24 w-full`} /></div>
    </div>
  );
}

// Provider profile hero skeleton
export function ProviderHeroSkeleton() {
  return (
    <div className="space-y-4" data-testid="skeleton-hero">
      <div className="grid grid-cols-3 gap-2 rounded-2xl overflow-hidden h-[320px]">
        <Skeleton className={`${S} col-span-2 h-full rounded-none`} />
        <div className="grid grid-rows-2 gap-2">
          <Skeleton className={`${S} h-full rounded-none`} />
          <Skeleton className={`${S} h-full rounded-none`} />
        </div>
      </div>
      <Skeleton className={`${S} h-12 w-full`} />
      <div className="space-y-3">
        <Skeleton className={`${S} h-3 w-24`} />
        <Skeleton className={`${S} h-8 w-64`} />
        <div className="flex gap-3">
          <Skeleton className={`${S} h-4 w-32`} />
          <Skeleton className={`${S} h-4 w-24`} />
          <Skeleton className={`${S} h-4 w-36`} />
        </div>
      </div>
    </div>
  );
}

// Chat message skeleton
export function ChatSkeleton() {
  return (
    <div className="space-y-4 py-4" data-testid="skeleton-chat">
      <div className="flex gap-2.5">
        <Skeleton className={`${S} w-7 h-7`} />
        <Skeleton className={`${S} h-20 w-3/4 rounded-2xl`} />
      </div>
      <div className="flex justify-end">
        <Skeleton className={`${S} h-10 w-1/2 rounded-2xl`} />
      </div>
      <div className="flex gap-2.5">
        <Skeleton className={`${S} w-7 h-7`} />
        <Skeleton className={`${S} h-32 w-4/5 rounded-2xl`} />
      </div>
    </div>
  );
}

// Invoice skeleton
export function InvoiceSkeleton() {
  return (
    <div className="bg-card border border-border/70 rounded-2xl shadow-soft" data-testid="skeleton-invoice">
      <div className="p-8 border-b border-border/70 flex justify-between">
        <div className="space-y-2"><Skeleton className={`${S} h-6 w-24`} /><Skeleton className={`${S} h-3 w-36`} /></div>
        <div className="space-y-2 text-right"><Skeleton className={`${S} h-5 w-20 ml-auto`} /><Skeleton className={`${S} h-3 w-28 ml-auto`} /></div>
      </div>
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-3 gap-6">
          {[1,2,3].map(i => <div key={i} className="space-y-2"><Skeleton className={`${S} h-2.5 w-12`} /><Skeleton className={`${S} h-3 w-28`} /><Skeleton className={`${S} h-3 w-24`} /></div>)}
        </div>
        <Skeleton className={`${S} h-16 w-full`} />
      </div>
    </div>
  );
}
